import React, {useContext, useEffect, useState} from 'react';
import {
  Text,
  TextTag,
  TokenTextAppearance,
  MultiSelect,
  Slider,
  TextInput,
  CTA,
  styled,
  TokenTextColor,
  Container,
  ContainerGutter,
  ContainerPadding
} from '@volkswagen-onehub/components-core';
import {useFeatureServices} from '../use-feature-services';
import Loader from './Loader';
import { AppContext } from '../app-context';
import { formatCurrency, formatDistance } from '../utils/format';
import { Language } from '../types/language';
import { LocationContext } from '../store/context/LocationContext';

const SearchWrapper = styled.div`
  position: relative;
  background: #061f4d;
  color: white;

  [class*='StyledContainer']:first-of-type {
    display: block;
  }

  div[role='slider'],
  [class*='StyledRailFill'] {
    background: #0040c5;
  }
`;

const ControlWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  color: white !important;
  margin-top: 15px;
  margin-bottom: 15px;
  align-items: flex-end;
  row-gap: 20px;

  > div,
  > fieldset,
  > label {
    width: 30%;
    display: inline-block;

    @media only screen and (max-width: 1023px) {
      width: 100%;
      margin: 15px 0px;
    }
  }

  [class*='StyledTextComponent'],
  [class*='StyledSmallLabelText'],
  [class*='StyledIcon'] {
    color: white;
  }

  [class*='StyledCharacterCounter'] {
    display: none;
  }

  [class*='StyledFocusWrapper'] {
    [class*='StyledTextComponent'] {
      color: rgb(0, 14, 38);
    }
  }

  [class*='StyledValueBubble'] {
    [class*='StyledTextComponent'] {
      color: rgb(0, 64, 197);
    }
  }

  [class*='StyledInput'] {
    color: white;
    box-shadow: none !important;
    border-bottom: 1px solid white;
    &:focus:not(:read-only) {
      color: white;
    }
    &::placeholder {
      color: white;
      opacity: 0.8;
    }
    &:hover,
    &:focus {
      border-bottom-color: white;
    }
  }

  [class*='StyledInputLineWrapper'] {
    border-bottom: 0px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: 30px;
  margin-bottom: 15px;

  @media only screen and (max-width: 1023px) {
    margin: 0px;
  }

  button {
    background: white;
    color: rgb(0, 30, 80);
    border: 1px solid rgb(0, 30, 80);

    &:hover {
      color: white;
      background: rgb(0, 30, 80);
      transition: all 0.2s ease-in-out 0s;
    }
  }

  [disabled] {
    &:hover {
      border: 1px solid white;
    }
  }
`;

const SelectWrapper = styled.div`
  [class*='StyledInteractionWrapper'] {
    border-bottom-color: white;
  }

  [class*='StyledOverlay'] {
    margin-top: 0px;
    height: 200px;
    overflow: scroll;
  }
  fieldset {
    margin-bottom: 30px;
  }
`;
const MoveTitle = styled.div`
  [class*='StyledTextComponent'] {
    margin-bottom: '24px';
    display: block;
  }
`;
const KmWrapper = styled.div`
  [class*='StyledValueBubble'] {
    [class*='StyledTextComponent'] {
      color: rgb(0, 14, 38);
    }
  }

  [class*='StyledMarks'] {
    [class*='StyledTextComponent'] {
      color: white;
    }
  }

  [class*='StyledThumb'] {
    &:active,
    &:focus {
      ::before {
        border: 2px solid white;
        opacity: 1;
      }
    }
  }
`;

const DollarWrapper = styled.div`
  [class*='StyledValueBubble'] {
    [class*='StyledTextComponent'] {
      color: rgb(0, 14, 38);
    }
  }

  [class*='StyledMarks'] {
    [class*='StyledTextComponent'] {
      color: white;
    }
  }

  [class*='StyledThumb'] {
    &:active,
    &:focus {
      ::before {
        border: 2px solid white;
        opacity: 1;
      }
    }
  }
`;

const PostalWrapper = styled.div`
  [id*='information'] {
    background: rgb(228, 0, 44);
  }
`;
interface Props {
  title: string;
  searchType: string;
  language: string;
}

const SearchBar = (props: Props) => {
  const context = useContext(AppContext);
  const {tracking} = useFeatureServices();
  const [selectedModel, setSelectedModel] = useState<string[]>([]);
  const [postalCode, setPostalCode] = useState<string | null>(null);
  const [jsonRes, setJsonRes] = useState<any>({});
  const [validPostal, setValidPostal] = useState(false);
  const [selectedModelOptions, setSelectedModelOptions] = useState({});
  const [maxMileage, setMaxMileage] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);

  const locationCtx = useContext(LocationContext);

  let labels = require('../lang/en.json');
  if (props.language != 'en') {
    labels = require('../lang/fr.json');
  }

  useEffect(() => {
    tracking.track('VWBasic_GenericFeatureApp_Load', {
      eventInfo: {
        brand: 'passenger',
        pageName: window.location,
      },
      environment: {
        featureAppId: 'MiniInventoryTool-D6-CA',
      },
      user: {
        loginStatus: false,
      },
    });

    window.onkeydown = (ev: KeyboardEvent): any => {
      if (ev.code == 'Enter') {
        document
          .getElementById('ButtonWrapper')
          ?.getElementsByTagName('button')[0]
          .click();
      }
    };
  }, []);

  useEffect(() => {
    if(!locationCtx?.value) return;
    setPostalCode(locationCtx?.value);
  }, [locationCtx?.value]);

  async function getAPIData() {
    const APIUrl = `${context.globalApi}/inventory-app/filters`;
    const response = await fetch(APIUrl, {
      headers: {
        'Content-Language': props.language,
      },
    });

    setJsonRes(await response.json());
  }

  useEffect(() => {
    getAPIData();
  }, [props.searchType]);

  useEffect(() => {
    if (Object.keys(jsonRes).length > 0) {
      const optionList: {[key: string]: string} = {};
      const order = jsonRes['model']
        .map((model: any) => model.label[props.language])
        .sort();

      order.forEach((x: string) => {
        const model = jsonRes['model'].filter(
          (y: any) => y.label[props.language] === x,
        )[0];
        optionList[model.value as string] = model.label[
          props.language
        ] as string;
      });

      setSelectedModelOptions(optionList);
      setMaxMileage(Math.round(jsonRes['mileage']['max']/1000)*1000);
      setMaxPrice(Math.round(jsonRes['price']['max']/1000)*1000);
    }
  }, [jsonRes]);

  const generateURL = () => {
    let searchLink;
    if (props.language == 'en') {
      searchLink = `${context.inventoryToolBaseUrl}/en/inventory.html/results?`;
    } else {
      searchLink = `${context.inventoryToolBaseUrl}/fr/inventaire.html/results?`;
    }

    if (selectedModel.length) {
      searchLink += 'model=';
      searchLink += selectedModel.join(',');
      searchLink += '&';
    }
    if (maxMileage != 0 && maxMileage != Math.round(jsonRes['mileage']['max']/1000)*1000) {
      searchLink += 'mileage=' + maxMileage.toString() + '&';
    }
    if (
      maxPrice != 1000 &&
      maxPrice != Math.round(jsonRes['price']['max']/1000)*1000 &&
      maxPrice != 0
    ) {
      searchLink += 'price=' + maxPrice.toString() + '&';
    }
    if (postalCode != '') {
      if (validPostal) {
        searchLink += 'postal_code=' + postalCode + '&';
      }
    }

    searchLink += 'category=' + props.searchType;
    return searchLink;
  };

  function testPostalCode() {
    if (postalCode) {
      // postal codes do not include D, F, I, O, Q, or U, and first position cannot be W or Z
      // space in fourth position is optional
      // https://www150.statcan.gc.ca/n1/pub/92-154-g/2015001/tech-eng.htm

      const regex = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

      if(regex.test(postalCode)) {
        setValidPostal(true);
        locationCtx?.set(postalCode);
      } else {
        setValidPostal(false);
      }
    }
  }

  useEffect(() => {
    if(!postalCode) return;

    testPostalCode();
  }, [postalCode]);

  const getCTACopy = () => {
    if (selectedModel.length && postalCode) {
      if (!(postalCode.length <= 0 || !validPostal)) {
        return labels.ctaText;
      } else {
        return labels.ctaPostalErr;
      }
    }
    return labels.ctaModelErr;
  };

  if (Object.keys(jsonRes).length > 0 && selectedModelOptions)
    return (
      <SearchWrapper>
        <Container
          gutter={ContainerGutter.static150}
          padding={ContainerPadding.static500}
        >
          <Text
            tag={TextTag.h1}
            appearance={TokenTextAppearance.headline400}
            color={TokenTextColor.inherit}
          >
            {props.title}
          </Text>

          <ControlWrapper>
            <SelectWrapper>
              <MoveTitle>
                <Text
                  tag={TextTag.span}
                  appearance={TokenTextAppearance.copy100}
                  bold
                >
                  {' '}
                  {labels.modelOuter}{' '}
                </Text>
              </MoveTitle>
              <MultiSelect
                name="selectedServices"
                label={labels.modelLabel}
                isFloating
                options={selectedModelOptions}
                onChange={(value) => {
                  tracking.track('VWBasic_FilterSelect_Click', {
                    eventInfo: {
                      brand: 'passenger',
                      pageId: window.location,
                    },
                    environment: {
                      featureAppId: 'MiniInventoryTool-D6-CA',
                      language: props.language,
                    },
                    user: {
                      loginStatus: false,
                    },
                    filter: {
                      type: 'multi_value',
                      name: 'Models',
                      values: value,
                    },
                  });
                  setSelectedModel(value as string[]);
                }}
              />
            </SelectWrapper>
            <KmWrapper>
              <Text
                tag={TextTag.span}
                appearance={TokenTextAppearance.copy100}
                bold
              >
                {' '}
                {labels.mileageOuter}{' '}
              </Text>
              <Slider
                defaultValue={Math.round(jsonRes['mileage']['max']/1000)*1000}
                min={Math.floor(jsonRes['mileage']['min']/1000)*1000}
                max={Math.round(jsonRes['mileage']['max']/1000)*1000}
                step={1000}
                ariaLabel={labels.mileageOuter}
                getValueText={(value) =>
                  formatDistance(props.language as Language, value)
                }
                onChange={(value) => {
                  tracking.track('VWBasic_FilterSelect_Click', {
                    eventInfo: {
                      brand: 'passenger',
                      pageId: window.location,
                    },
                    environment: {
                      featureAppId: 'MiniInventoryTool-D6-CA',
                      language: props.language,
                    },
                    user: {
                      loginStatus: false,
                    },
                    filter: {
                      type: 'range',
                      name: 'Mileage',
                      lowerbound: jsonRes['mileage']['min'],
                      upperbound: value,
                    },
                  });
                  setMaxMileage(value);
                }}
              />
            </KmWrapper>

            <DollarWrapper>
              <Text
                tag={TextTag.span}
                appearance={TokenTextAppearance.copy100}
                bold
              >
                {' '}
                {labels.priceOuter}{' '}
              </Text>
              <Slider
                defaultValue={Math.round(jsonRes['price']['max']/1000)*1000}
                min={Math.floor(jsonRes['price']['min']/1000)*1000}
                max={Math.round(jsonRes['price']['max']/1000)*1000}
                step={1000}
                ariaLabel={labels.priceOuter}
                getValueText={(value) =>
                  formatCurrency(props.language as Language, value)
                }
                onChange={(value) => {
                  tracking.track('VWBasic_FilterSelect_Click', {
                    eventInfo: {
                      brand: 'passenger',
                      pageId: window.location,
                    },
                    environment: {
                      featureAppId: 'MiniInventoryTool-D6-CA',
                      language: props.language,
                    },
                    user: {
                      loginStatus: false,
                    },
                    filter: {
                      type: 'range',
                      name: 'Price',
                      lowerbound: jsonRes['price']['min'],
                      upperbound: value,
                    },
                  });
                  setMaxPrice(value);
                }}
              />
            </DollarWrapper>
            <PostalWrapper>
              <Text
                tag={TextTag.span}
                appearance={TokenTextAppearance.copy100}
                bold
              >
                {' '}
                {labels.postalCodeLabel}
              </Text>
              <TextInput
                placeholder="A1A 1A1"
                message={labels.errMessage}
                appearance={
                  !validPostal
                    ? "information"
                    : "default"
                }
                customId="postalCodeInput"
                defaultValue={postalCode as string | undefined}
                onChange={(value) => {
                  setPostalCode(value.currentTarget.value);
                }}
                maxLength={7}
              />
            </PostalWrapper>
          </ControlWrapper>

          <ButtonWrapper id="ButtonWrapper">
            <CTA
              tag="button"
              data-testid="MiniSearchButton"
              onClick={() => {
                tracking.track('VWBasic_FilterSearchButton_Click', {
                  eventInfo: {
                    brand: 'passenger',
                    pageName: window.location,
                  },
                  environment: {
                    featureAppId: 'MiniInventoryTool-D6-CA',
                    language: props.language,
                  },
                  user: {
                    loginStatus: false,
                  },
                  link: {url: generateURL(), name: 'Search results'},
                  filter: {
                    filterlist: [
                      {
                        type: 'range',
                        name: 'PR',
                        lowerbound: jsonRes['price']['min'],
                        upperbound: maxPrice,
                      },
                      {
                        type: 'range',
                        name: 'KR',
                        lowerbound: jsonRes['mileage']['min'],
                        upperbound: maxMileage,
                      },
                      {
                        type: 'multi_value',
                        name: 'CG',
                        values: selectedModel,
                      },
                    ],
                  },
                });
                window.open(generateURL(), '_self');
              }}
              disabled={!selectedModel.length || !validPostal}
            >
              {getCTACopy()}
            </CTA>
          </ButtonWrapper>
        </Container>
      </SearchWrapper>
    );
  return <Loader />;
};

export default SearchBar;
